import { hashHistory } from "react-router";
import _ from "lodash";

import { getInstance as getDataSources } from "datasources";
import { getInstance as getStore } from "store/store";
import { getInstance as getStorageManager } from "storage/manager";
import AnalyticsEventsManager from "utils/firebase/index.js";
import { setPaymentRedirectLink } from "./newRegistration";
import { getSubscriptionPaymentMethod } from "../../utils/firebase/remoteConfig";
import { clearPaymentDetails, updatePaymentDetails } from "./paymentdetails";

// Action types
const ActionTypes = {
    SET_PAYMENT_STATUS: "SET_PAYMENT_STATUS",
    SET_PAYMENT_DATA: "SET_PAYMENT_DATA",
    SET_PAYMENT_ERROR: "SET_PAYMENT_ERROR",
  },
  // Status values
  PaymentStatus = {
    INITIAL: "initial",
    PROCESSING: "processing",
    PROCESSING_PAYMENT: "processing-payment",
    REQUEST_SUCCESS: "request-success",
    REQUEST_FAILURE: "request-failure",
  };

/**
 * Set status action.
 *
 * @param {string} status - New status of the component.
 * @returns {Object} Action type and the new status.
 */
function setStatus(status = PaymentStatus.INITIAL) {
  return {
    type: ActionTypes.SET_PAYMENT_STATUS,
    status,
  };
}

/**
 * Set error action.
 *
 * @param {Object} error - Error object with reason message.
 * @returns {Object} Action type and error object.
 */
function setError(error) {
  return {
    type: ActionTypes.SET_PAYMENT_ERROR,
    formError: error,
  };
}

/**
 * Subscribe.
 *
 * @returns {Object} Action type and error object.
 */
function subscribe() {
  return (dispatch) => {
    let dataSources = getDataSources(),
      store = getStore().getState(),
      { user } = store,
      { id: userId } = user,
      order = {
        price: 0,
        order: "",
      },
      failedRequestCallback = (response) => {
        let error = _.get(response, ["result", "error"]);

        if (error && (typeof error === "string" || error.message)) {
          error = typeof error === "string" ? error : error.message;
        } else {
          error = "Unknown Error";
        }

        dispatch(setError(error));
        dispatch(setStatus(PaymentStatus.REQUEST_FAILURE));
      };

    dispatch(setStatus(PaymentStatus.PROCESSING_PAYMENT));
    return dataSources
      .getConfig()
      .then((config) => {
        return (
          _.get(config, ["_presets", "subscriptionId"]) ||
          _.get(config, ["subscriptionId"])
        );
      })
      .then((subscriptionId) => {
        return dataSources.productPriceList(subscriptionId);
      })
      .then((priceList) => {
        let priceBeingChecked = priceList.length ? priceList[0] : {};

        if (priceBeingChecked.purchaseStatus === "entitled_to_preview_module") {
          order.price = 1;
          order.order = "FREE";
        } else {
          order.price = priceBeingChecked.price.amount;
          order.order = "SUBSCRIPTION";
        }

        // get default payment gateway
        return dataSources.listPaymentGateways().then((gateways) => {
          let defaultPaymentGateway = null;

          _.each(gateways, (gateway) => {
            if (gateway.isDefaultGateway()) {
              defaultPaymentGateway = gateway;
            }
          });

          // in case none is default, pick first one
          if (!defaultPaymentGateway && gateways.length) {
            defaultPaymentGateway = gateways[0];
          }

          return defaultPaymentGateway;
        }, failedRequestCallback);
      })
      .then((defaultGateway) => {
        return dataSources
          .configurePaymentGateway(defaultGateway, userId, order)
          .then((redirectUrl) => {
            let currentTmp = store.paymentDetails.tmp,
              tmp = currentTmp || 400,
              analytics = new AnalyticsEventsManager();

            if (!currentTmp) {
              analytics.sendYaadEmptyTmpEvent({
                tmp: currentTmp,
              });
            }

            if (redirectUrl) {
              if (_.includes(redirectUrl.toLowerCase(), "ccode=")) {
                console.trace(`window.location.href redirectUrl + '&tmp=3';`);

                const redirectLink = redirectUrl + "&tmp=3";
                // window.location.href = redirectUrl + '&tmp=3';

                dispatch(setPaymentRedirectLink(redirectLink));
              } else {
                console.trace(
                  `window.location.href redirectUrl + '&tmp=${tmp}';`,
                );

                const redirectLink = redirectUrl + `&tmp=${tmp}`;
                // window.location.href = redirectUrl + `&tmp=${tmp}`;

                dispatch(setPaymentRedirectLink(redirectLink));
              }
            }
          });
      })
      .catch(() => {
        dispatch(setStatus(PaymentStatus.REQUEST_FAILURE));
      });
  };
}

/**
 * Sends data about Yaad successful transaction to Kaltura.
 *
 * @param {Object} redirectData - Data extracted from redirect URL.
 * @param {string} redirectUrl - Complete redirect URL.
 * @returns {Object} Action type and error object.
 */
function processRedirect(redirectData, redirectUrl) {
  return (dispatch) => {
    let dataSources = getDataSources(),
      store = getStore().getState(),
      { user } = store,
      { id: userId } = user,
      subscriptionId,
      productPrice = {},
      paymentGateway,
      dashboardRedirectUrl = "/dashboard",
      successRedirectUrl = "/success",
      settingsRedirectUrl = "/settings",
      { Order: paymentOrder } = redirectData,
      isAddCard = paymentOrder.toString().includes("ADDCARD"),
      failedRequestCallback = (response) => {
        let error = _.get(response, ["result", "error"]);

        if (error && (typeof error === "string" || error.message)) {
          error = typeof error === "string" ? error : error.message;
        } else {
          error = "Unknown Error";
        }

        dispatch(setError(error));
      };

    dispatch(setStatus(PaymentStatus.PROCESSING_PAYMENT));
    return dataSources
      .getConfig()
      .then((config) => {
        subscriptionId = _.get(config, ["_presets", "subscriptionId"]);
        return subscriptionId;
      })
      .then((receivedSubscriptionId) => {
        return dataSources
          .productPriceList(receivedSubscriptionId)
          .then((priceList) => {
            let priceBeingChecked = priceList.length ? priceList[0] : {};

            productPrice.price = priceBeingChecked.price.amount;
            productPrice.currency = priceBeingChecked.price.currency;
            productPrice.productType = priceBeingChecked.productType;
          });
      })
      .then(() => {
        // get default payment gateway
        return dataSources.listPaymentGateways().then((gateways) => {
          let defaultPaymentGateway = null;

          _.each(gateways, (gateway) => {
            if (gateway.isDefaultGateway()) {
              defaultPaymentGateway = gateway;
            }
          });

          // in case none is default, pick first one
          if (!defaultPaymentGateway && gateways.length) {
            defaultPaymentGateway = gateways[0];
          }

          return defaultPaymentGateway;
        }, failedRequestCallback);
      })
      .then((gateway) => {
        let paymentMethodParams = {
          subscriptionId: subscriptionId,
          gatewayId: gateway.id,
          userId: userId,
          redirectUrl: redirectUrl,
        };

        paymentGateway = gateway;

        // add new payment method
        return dataSources.addPaymentMethod(paymentMethodParams);
      })
      .then((paymentConfiguration) => {
        if (isAddCard) {
          // skip purchase for add card
          return Promise.resolve();
        }

        let transactionPurchaseParams = {
          productId: subscriptionId,
          currency: productPrice.currency,
          price: productPrice.price,
          productType: productPrice.productType,
          paymentMethodId: paymentConfiguration.paymentMethodId,
          paymentGatewayId: paymentGateway.id,
          adapterData: redirectUrl,
        };

        // call transaction purchase

        return dataSources.transactionPurchase(transactionPurchaseParams);
      }).then(() => {
        return dataSources.getPaymentDetails()
          .then((paymentDetails) => {
            if (paymentDetails) {
              return getSubscriptionPaymentMethod().then((subscriptionPaymentMethods) => {
                let isNeedHideCardSection = false;
                const parsedSubscriptionPaymentMethods = JSON.parse(subscriptionPaymentMethods._value);

                Object.keys(parsedSubscriptionPaymentMethods).forEach((paymentMethod) => {
                  if (paymentMethod === "APPLE" && parsedSubscriptionPaymentMethods[paymentMethod].includes(Number(paymentDetails.subscriptionId))) {
                    isNeedHideCardSection = true;
                  }
                });
                paymentDetails.isNeedHideCardSection = isNeedHideCardSection;
                return paymentDetails;
              });
            }

            return false;
          }).then(paymentDetails => {
            let store = getStore();

            if (paymentDetails) {
              store.dispatch(updatePaymentDetails(paymentDetails));
            } else {
              store.dispatch(clearPaymentDetails());
            }
            return Promise.resolve();
          });
      })
      .then(() => {
        // if (isMobile()) {
        //     switch (operatingSystem()) {
        //         case OperatingSystems.ios:
        //             startIOSApp();
        //             break;
        //         default:
        //             startAndroidApp();
        //     }
        // }

        if (isAddCard) {
          console.trace("hashHistory.push");
          hashHistory.push(settingsRedirectUrl);
        } else {
          const storageManager = getStorageManager();
          const analytics = new AnalyticsEventsManager();

          storageManager.store("registration.success", "true");
          console.trace(
            "hashHistory.push",
            "successRedirectUrl",
            successRedirectUrl,
          );
          analytics.newRegistrationStep_9Achieved();
          analytics.subscriptionPurchased();
          hashHistory.push(successRedirectUrl);
        }
      });
  };
}

// Exposed elements of the actions file.
export { ActionTypes, PaymentStatus, setStatus, subscribe, processRedirect };



// WEBPACK FOOTER //
// ./src/js/store/actions/payment.js